/**
 * Science.co growth model: the defaults.
 *
 * Transcribed from "Science.co Model 2026-2027_Conservative.xlsx" (Assumptions,
 * Recurring Cohorts and the P&L sheet). Every per-month array is indexed by
 * MODEL MONTH, not calendar month: index 0 is whatever `startMonth` says, so
 * moving the start slides the whole plan without re-keying anything.
 *
 * The sheet dates its columns Oct 2026 – Dec 2027 but numbers them from
 * Jul 2026 in a helper row. Science.co took its first subscriptions in August
 * and ramped in September, so September is the agreed Month 1 (2026-09-17).
 *
 * Edits in the page are session-only — this file is the checked-in plan. It is
 * Science.co's model and nothing else: the general-purpose version waits until
 * this one has taught us what a model needs to carry.
 */

/** Plan lengths, in the order the sheet lists them. Keys are shared with the API. */
export const PLANS = ['annual', 'six_month', 'three_month', 'monthly']

export const PLAN_LABELS = {
  annual: '1-year',
  six_month: '6-month',
  three_month: '3-month',
  monthly: 'Monthly',
}

/**
 * The rate cards the model plans against.
 *
 * Not the Daily Report's SALES_REGISTERS: `email` is a register but not a rate
 * card — Customer.io's win-back link sells on the phone room's prices — so the
 * model carries three books and the actuals fold `email` into `sales_team`.
 */
export const BOOKS = ['web', 'sales_team', 'reddit']

export const BOOK_LABELS = { web: 'Web', sales_team: 'Sales Team', reddit: 'Reddit' }

export const MOLECULES = ['tirzepatide', 'semaglutide']

export const MODEL_MONTHS = 15

const flat = (value) => Array(MODEL_MONTHS).fill(value)

export const SCIENCE_MODEL_DEFAULTS = {
  startMonth: '2026-09',

  acquisition: {
    // Month 1's total spend; later months compound it by `spendGrowth`.
    startingSpend: 500000,
    // Index 0 is unused — month 1 has no prior month to grow from.
    spendGrowth: [0, 0.65, 0.6, 0.45, 0.3, 0.2, 0.2, 0.2, 0.1, 0.1, 0.1, 0.05, 0.05, 0.05, 0.05],
    // Share of spend that is paid media; the rest is "organic" spend.
    paidShare: [0.9, 0.91, 0.92, 0.93, 0.95, 0.965, 0.965, 0.965, 0.965, 0.965, 0.965, 0.965, 0.965, 0.965, 0.965],
    cacPaid: [280, 280, 270, 270, 250, 250, 250, 250, 250, 250, 250, 250, 250, 250, 250],
    cacOrganic: [30, 30, 30, 30, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20],
  },

  // Of new customers. Constant across the horizon in the sheet.
  planSplit: { annual: 0.05, six_month: 0.1, three_month: 0.3, monthly: 0.55 },

  moleculeMix: { tirzepatide: 0.6, semaglutide: 0.4 },

  // Which checkout's prices apply, per model month. Web grows as Reddit fades.
  bookWeights: {
    web: [0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.85, 0.85, 0.85, 0.85, 0.85, 0.85, 0.85, 0.85, 0.85],
    sales_team: flat(0.1),
    reddit: [0.6, 0.5, 0.4, 0.3, 0.2, 0.1, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05, 0.05],
  },

  // Price per charge (a 3-month plan is one $399 charge, not $399 a month).
  prices: {
    tirzepatide: {
      annual: { web: 1500, sales_team: 1380, reddit: 1068 },
      six_month: { web: 774, sales_team: 720, reddit: 564 },
      three_month: { web: 399, sales_team: 375, reddit: 299 },
      monthly: { web: 139, sales_team: 138, reddit: 129 },
    },
    semaglutide: {
      annual: { web: 948, sales_team: 840, reddit: 828 },
      six_month: { web: 486, sales_team: 450, reddit: 444 },
      three_month: { web: 250, sales_team: 240, reddit: 237 },
      monthly: { web: 89, sales_team: 88, reddit: 87 },
    },
  },

  // Share of a cohort that PAYS in model month k after purchase (k = 0 is the
  // purchase itself). A 3-month plan only pays on its renewal months, hence
  // the zeros between. Ages past the end of an array are zero.
  retention: {
    annual: [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    six_month: [1, 0, 0, 0, 0, 0, 0.5, 0, 0, 0, 0, 0, 0.25],
    three_month: [1, 0, 0, 0.5, 0, 0, 0.25, 0, 0, 0.1, 0, 0, 0],
    monthly: [1, 0.7, 0.6, 0.5, 0.4, 0.2, 0.1, 0.05, 0.05, 0, 0, 0, 0],
  },

  // Per charge. Product cost scales with plan length; shipping and the doctor
  // visit are charged once per paying customer per month, as the sheet does.
  unitCosts: {
    product: { annual: 280, six_month: 140, three_month: 70, monthly: 25 },
    shipping: 20,
    doctorVisit: 8,
  },

  rates: {
    refunds: 0.025, // of revenue
    cogsOther: 0.01, // of product COGS
    merchant: 0.025, // of income
    agency: 0.1, // of ad spend
    management: 0.03, // of income
    // Paid on the Sales Team and Reddit share of revenue.
    commission: 0.05,
  },

  // Sheet hard-codes the first months before the rate takes over.
  overrides: {
    agency: [20000, 25000],
    management: [75000, 75000, 75000],
  },

  fixedMonthly: {
    legal: 5000,
    marketingFees: 10000,
    office: 300,
    supplies: 200,
    software: 40000,
    payroll: 0,
    rent: 0,
    insurance: 0,
  },
}
